<!--
AssistantMessageDiagnostics — collapsible, PII-FREE diagnostics panel rendered
under each assistant turn.

Presentational only: it receives an already-validated `AgentDiagnostics` object
(parsed by the composable's boundary guard, which strips everything but the
PII-free fields) and renders it. It renders ONLY tokens, per-tool
name+outcome+latency, finishReason, requestId and iterations — NEVER raw tool
args or backend response bodies. This mirrors the SECURITY INVARIANT enforced on
both ends: diagnostics are PII-free by construction.

Uses <Panel> (the transparent PrimeVue Panel pass-through) for the toggleable
collapse; `toggleable`/`:collapsed`/`header` ride through `$attrs`.
-->
<template>
  <Panel
    :header="t('Diagnóstico')"
    toggleable
    :collapsed="true"
    class="assistant-diagnostics"
    data-testid="assistant-diagnostics"
  >
    <dl class="assistant-diagnostics-grid">
      <div class="assistant-diagnostics-row">
        <dt>{{ t('Tokens (prompt / resposta / total)') }}</dt>
        <dd data-testid="assistant-diagnostics-tokens">
          {{ formatTokens(diagnostics.promptTokens) }} /
          {{ formatTokens(diagnostics.completionTokens) }} /
          {{ formatTokens(diagnostics.totalTokens) }}
        </dd>
      </div>

      <div class="assistant-diagnostics-row">
        <dt>{{ t('Motivo de término') }}</dt>
        <dd data-testid="assistant-diagnostics-finish-reason">{{ diagnostics.finishReason }}</dd>
      </div>

      <div class="assistant-diagnostics-row">
        <dt>{{ t('Iterações') }}</dt>
        <dd>{{ diagnostics.iterations }}</dd>
      </div>

      <div class="assistant-diagnostics-row">
        <dt>{{ t('ID da requisição') }}</dt>
        <dd data-testid="assistant-diagnostics-request-id">{{ diagnostics.requestId }}</dd>
      </div>
    </dl>

    <div v-if="diagnostics.tools.length > 0" class="assistant-diagnostics-tools">
      <h4 class="assistant-diagnostics-tools-title">{{ t('Ferramentas') }}</h4>
      <ul class="assistant-diagnostics-tool-list">
        <li
          v-for="(tool, toolIndex) in diagnostics.tools"
          :key="toolIndex"
          class="assistant-diagnostics-tool"
          :data-testid="`assistant-diagnostics-tool-${toolIndex}`"
        >
          <span class="assistant-diagnostics-tool-name">{{ tool.name }}</span>
          <span
            class="assistant-diagnostics-tool-outcome"
            :class="tool.outcome === 'ok'
              ? 'assistant-diagnostics-tool-ok'
              : 'assistant-diagnostics-tool-error'"
          >{{ tool.outcome }}</span>
          <span v-if="tool.errorCode" class="assistant-diagnostics-tool-code">{{ tool.errorCode }}</span>
          <span class="assistant-diagnostics-tool-latency">{{ tool.latencyMs }} ms</span>
        </li>
      </ul>
    </div>
  </Panel>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import Panel from '../../ui/Panel.vue';
import type { AgentDiagnostics } from '../../composables/aiAgentContract';

defineProps<{
  diagnostics: AgentDiagnostics;
}>();

const { t } = useI18n();

function formatTokens(value: number | null): string {
  return value === null ? '—' : String(value);
}
</script>

<style scoped>
.assistant-diagnostics {
  margin-top: 0.5rem;
}

.assistant-diagnostics-grid {
  margin: 0;
  display: grid;
  gap: 0.25rem;
}

.assistant-diagnostics-row {
  display: flex;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.assistant-diagnostics-row dt {
  font-weight: 600;
  color: var(--p-text-muted-color);
  margin: 0;
}

.assistant-diagnostics-row dd {
  margin: 0;
  word-break: break-all;
}

.assistant-diagnostics-tools {
  margin-top: 0.5rem;
}

.assistant-diagnostics-tools-title {
  margin: 0 0 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
}

.assistant-diagnostics-tool-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assistant-diagnostics-tool {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.assistant-diagnostics-tool-name {
  font-family: monospace;
}

.assistant-diagnostics-tool-outcome {
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 700;
}

.assistant-diagnostics-tool-ok {
  color: var(--app-success-color);
}

.assistant-diagnostics-tool-error {
  color: var(--app-danger-color);
}

.assistant-diagnostics-tool-latency {
  margin-left: auto;
  color: var(--p-text-muted-color);
}
</style>
